import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';

export enum OperationType {
	OCR_FRONT = 'OCR_FRONT',
	OCR_BACK = 'OCR_BACK',
	OCR_COMPLETE = 'OCR_COMPLETE',
	OCR_MANUAL_BACK = 'OCR_MANUAL_BACK',
	OCR_MANUAL_FRONT = 'OCR_MANUAL_FRONT',
	OCR_MANUAL_COMPLETE = 'OCR_MANUAL_COMPLETE',
	POL = 'POL',
	POL_MANUAL = 'POL_MANUAL',
	DOCUMENTS = 'DOCUMENTS',
	FLOW_COMPLETE = 'FLOW_COMPLETE',
}

export interface ISDKEventData {
	operationType: OperationType;
	timestamp?: string;
	status?: string;
	[key: string]: any;
}

@Injectable({
	providedIn: 'root',
})
export class IdentitySDKService {
	// Observable para tracking interno (opcional)
	private eventSubject = new Subject<{ event: string; data?: any }>();
	public events$ = this.eventSubject.asObservable();

	// Flag interno que indica si el SDK está habilitado
	private sdkEnabled: boolean = false;

	// Flag para controlar que FLOW_COMPLETE solo se notifique una vez por proceso
	private flowCompleteNotified: boolean = false;

	/**
	 * Establece si el SDK está habilitado o no
	 * Este método debe ser llamado cuando se recibe la configuración del proceso
	 * @param enabled - true si el SDK debe estar activo, false en caso contrario
	 */
	public setEnabled(enabled: boolean): void {
		this.sdkEnabled = enabled;
		// Resetear el flag de FLOW_COMPLETE cuando se inicia un nuevo proceso
		this.flowCompleteNotified = false;
	}

	/**
	 * Verifica si el SDK está habilitado
	 * @returns true si el SDK está habilitado, false en caso contrario
	 */
	public isEnabled(): boolean {
		return this.sdkEnabled;
	}

	/**
	 * Maneja la devolución de llamada del SDK.
	 *
	 * Determina el estado basado en el nombre del evento, envía un mensaje al padre (iframe)
	 * y emite un evento interno para seguimiento.
	 *
	 * @param eventName - El nombre del evento del SDK.
	 * @param data - Datos opcionales asociados al evento.
	 */
	private handleSDKCallback(eventName: string, data?: any): void {
		const status = eventName.includes('Success') ? 'SUCCESS' : 'ERROR';

		// Enviar al parent (iframe)
		window.parent.postMessage(
			{
				code: 'identity-sdk',
				event: eventName,
				status,
				data,
			},
			'*',
		);

		// Emitir evento interno para tracking
		this.eventSubject.next({ event: eventName, data });
	}

	/**
	 * Envía un evento al padre nativo mediante la llamada a la función de callback del SDK.
	 *
	 * @param eventName - El nombre del evento a enviar.
	 * @param data - Los datos asociados al evento.
	 */
	private sendToNative(eventName: string, data: any): void {
		// Lanzar eventos al parent
		this.handleSDKCallback(eventName, data);
	}

	/**
	 * Notifica al SDK nativo cuando una operación se completa exitosamente
	 * @param operationType - Tipo de operación completada
	 * @param data - Datos adicionales de la operación
	 */
	public notifyOperationSuccess(operationType: OperationType, data?: Partial<ISDKEventData>): void {
		if (!this.sdkEnabled) return;

		const payload: ISDKEventData = {
			operationType,
			timestamp: new Date().toISOString(),
			status: 'SUCCESS',
			...data,
		};

		this.sendToNative('onOperationSuccess', payload);

		// Emitir evento interno para tracking
		this.eventSubject.next({ event: 'onOperationSuccess', data: payload });
	}

	/**
	 * Notifica al SDK nativo cuando una operación falla
	 * @param operationType - Tipo de operación que falló
	 * @param error - Información del error
	 * @param data - Datos adicionales del error
	 */
	public notifyOperationError(
		operationType: OperationType,
		error: string | object,
		data?: Partial<ISDKEventData>,
	): void {
		if (!this.sdkEnabled) return;

		const payload: ISDKEventData = {
			operationType,
			timestamp: new Date().toISOString(),
			status: 'ERROR',
			error: typeof error === 'string' ? error : JSON.stringify(error),
			...data,
		};

		this.sendToNative('onOperationError', payload);

		// Emitir evento interno para tracking
		this.eventSubject.next({ event: 'onOperationError', data: payload });
	}

	/**
	 * Notifica al SDK nativo cuando el proceso completo se completa exitosamente
	 * Este método se llama cuando todo el flujo de identificación finaliza con éxito
	 * @param data - Datos del proceso completado
	 */
	public notifyProcessSuccess(data?: any): void {
		if (!this.sdkEnabled) return;

		const payload = {
			timestamp: new Date().toISOString(),
			status: 'SUCCESS',
			...data,
		};

		this.sendToNative('onProcessSuccess', payload);

		// Emitir evento interno para tracking
		this.eventSubject.next({ event: 'onProcessSuccess', data: payload });
	}

	/**
	 * Notifica al SDK nativo cuando el proceso completo falla
	 * Este método se llama cuando el flujo de identificación termina con error
	 * @param error - Información del error
	 * @param data - Datos adicionales del error
	 */
	public notifyProcessError(error: string | object, data?: any): void {
		if (!this.sdkEnabled) return;

		const payload = {
			timestamp: new Date().toISOString(),
			status: 'ERROR',
			error: typeof error === 'string' ? error : JSON.stringify(error),
			...data,
		};

		this.sendToNative('onProcessError', payload);

		// Emitir evento interno para tracking
		this.eventSubject.next({ event: 'onProcessError', data: payload });
	}

	/**
	 * Notifica al SDK que el flujo completo ha terminado (FLOW_COMPLETE)
	 * Este método determina automáticamente si fue éxito o error basándose en el status
	 * y garantiza que solo se notifique una vez por proceso
	 * @param status - Estado del proceso ('OK', 'KO', etc.)
	 * @param data - Datos adicionales del proceso
	 */
	public notifyFlowComplete(status: string, data?: any): void {
		if (!this.sdkEnabled) return;

		// Verificar si ya se notificó FLOW_COMPLETE para este proceso
		if (this.flowCompleteNotified) {
			return;
		}

		// Marcar como notificado
		this.flowCompleteNotified = true;

		// Determinar si es éxito o error basándose en el status
		// FLOW_COMPLETE usa notifyProcess
		if (status === 'OK') {
			this.notifyProcessSuccess({
				status: status,
				flowComplete: true,
				...data,
			});
		} else {
			this.notifyProcessError(`Process finished with status: ${status}`, {
				status: status,
				flowComplete: true,
				...data,
			});
		}
	}

	/**
	 * Resetea el flag de FLOW_COMPLETE
	 * Útil si se necesita reiniciar el estado para un nuevo proceso
	 */
	public resetFlowComplete(): void {
		this.flowCompleteNotified = false;
	}
}
